import React, { useCallback, useMemo } from "react";
import { styled } from "@mui/material/styles";

import {
  Button,
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

export interface DialogProps {
  /** whether the dialog is currently open */
  open: boolean;
  /** The title that appears in the header of the dialog */
  title: string;
  /** the size of the dialog, defaults to small */
  size?: "small" | "medium" | "large";
  /** the text that appears in the primary button, defaults to "Confirm" */
  confirmText?: string;
  /** The text that appears in the secondary button, defaults to "Cancel" */
  cancelText?: string;
  /** error text which should appear at the bottom of the dialog,
   * should be supplied when the confirmation action fails */
  errorText?: string;
  /** If true, the dialog actions will be disabled */
  actionsDisabled?: boolean;
  /** If true, the confirm button will be disabled */
  confirmDisabled?: boolean;
  /** If true, the cancel action will be disabled */
  cancelDisabled?: boolean;
  /** if enabled, calls the close callback when the user clicks outside the dialog*/
  allowClickaway?: boolean;
  /** Callback used when the confirm button is clicked. You should not combine uses of onConfirm and onSubmit. */
  onConfirm?: () => void;
  /** Callback to use when using the dialog as a form and the confirm button is clicked.
   *  You should not combine uses of onConfirm and onSubmit.  */
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  /** Callback used when cancel button clicked, if not provided onClose will be called instead */
  onCancel?: () => void;
  /** Callback used when user tries to close the dialog.
   * Also called if the user clicks off of the dialog when allowClickaway is enabled.*/
  onClose(): void;
  fullWidth?: boolean;
  confirmButton?: JSX.Element;
  dialogId: string;
}

export const Dialog = ({
  open,
  title,
  size = "small",
  confirmText = "Confirm",
  cancelText = "Cancel",
  errorText,
  onSubmit,
  onConfirm,
  onCancel,
  actionsDisabled = false,
  confirmDisabled = false,
  cancelDisabled = false,
  onClose,
  allowClickaway = false,
  children,
  fullWidth = true,
  confirmButton,
  dialogId
}: React.PropsWithChildren<DialogProps>): JSX.Element => {
  const maxWidth = useMemo(() => {
    let maxWidth: "xs" | "sm" | "md" = "xs";

    if (size !== "small") {
      if (size === "large") {
        maxWidth = "md";
      } else {
        maxWidth = "sm";
      }
    }

    return maxWidth;
  }, [size]);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      // Prevent default because we never want the form to be posted automatically.
      // Instead we handle the submission using the onSubmit function passed in the component properties.
      event.preventDefault();
      onSubmit?.(event);
    },
    [onSubmit]
  );

  const formId = `${dialogId}-form`;

  return (
    <MuiDialog
      id={dialogId}
      open={open}
      maxWidth={maxWidth}
      onClose={() => allowClickaway && onClose()}
      fullWidth={fullWidth}
    >
      <DialogTitle>
        <DialogHeader>
          <BreakTypography variant="h6">{title}</BreakTypography>
          <IconButton
            aria-label="close dialog"
            disabled={cancelDisabled}
            onClick={onClose}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </DialogHeader>
      </DialogTitle>

      {children && (
        <DialogContent dividers>
          <form id={formId} onSubmit={handleSubmit} noValidate>
            {children}
          </form>
        </DialogContent>
      )}

      <DialogActions>
        {errorText && (
          <Typography variant="body2" color="error">
            {errorText}
          </Typography>
        )}
        <StyledButton
          variant="outlined"
          color="primary"
          onClick={() => (onCancel ? onCancel() : onClose())}
          disabled={actionsDisabled || cancelDisabled}
        >
          {cancelText}
        </StyledButton>
        {confirmButton ? (
          confirmButton
        ) : (
          <StyledButton
            variant="contained"
            color="primary"
            type="submit"
            form={formId}
            onClick={() => onConfirm && onConfirm()}
            disabled={actionsDisabled || confirmDisabled}
          >
            {confirmText}
          </StyledButton>
        )}
      </DialogActions>
    </MuiDialog>
  );
};

const BreakTypography = styled(Typography)`
  word-break: break-word;
`;

const DialogHeader = styled("div")`
  display: flex;
  flex-grow: 1;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${props => props.theme.spacing(1)};
`;

const StyledButton = styled(Button)`
  white-space: nowrap;
`;
